/**
 * Settings Page.
 *
 * Tabbed admin interface for managing per-organization configuration.
 * Each section can be independently edited and reset to defaults.
 */

import { useState, useEffect, useCallback } from "react";
import {
  RotateCcw,
  Save,
  Plus,
  Trash2,
  GripVertical,
  Pencil,
  X,
  Settings2,
} from "lucide-react";
import {
  useOrgConfig,
  useUpdateSection,
  useResetSection,
} from "@/features/settings/api";
import { toast } from "@/stores/toastStore";
import type {
  MakeOption,
  ModelOption,
  NamingConvention,
  NamingComponent,
  VariantOption,
  OrgConfig,
} from "@/types/settings";
import CategoryManager from "@/features/settings/CategoryManager";
import LocationManager from "@/features/settings/LocationManager";
import LocationSchemeManager from "@/features/yards/LocationSchemeManager";
import { useBusinessLocations } from "@/features/locations/api";
import MakeEditor from "@/features/settings/MakeEditor";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import Card from "@/components/ui/Card";
import LoadingSpinner from "@/components/ui/LoadingSpinner";

type Tab =
  | "business"
  | "products"
  | "locations"
  | "number_formats"
  | "purchases"
  | "work_orders"
  | "sales"
  | "roles";

const tabs: { key: Tab; label: string }[] = [
  { key: "business", label: "Business Profile" },
  { key: "products", label: "Products" },
  { key: "locations", label: "Facilities" },
  { key: "number_formats", label: "Number Formats" },
  { key: "purchases", label: "Purchases" },
  { key: "work_orders", label: "Work Orders" },
  { key: "sales", label: "Sales" },
  { key: "roles", label: "Roles" },
];

function SettingsPage() {
  const { data: config, isLoading } = useOrgConfig();
  const [activeTab, setActiveTab] = useState<Tab>("business");

  if (isLoading || !config)
    return <LoadingSpinner className="py-24" size="lg" />;

  return (
    <div className="space-y-6">
      <div className="border-b border-[var(--border-default)]">
        <nav className="flex gap-6 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`whitespace-nowrap border-b-2 pb-3 text-sm font-medium transition-colors ${activeTab === tab.key ? "border-[var(--color-interactive-primary)] text-[var(--color-interactive-primary)]" : "border-transparent text-[var(--text-secondary)] hover:text-[var(--text-secondary)]"}`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === "business" && (
        <BusinessSection
          config={config}
          onNavigateToLocations={() => setActiveTab("locations")}
        />
      )}
      {activeTab === "products" && <ProductsSection config={config} />}
      {activeTab === "locations" && <LocationManagerTab />}
      {activeTab === "number_formats" && (
        <NumberFormatsSection config={config} />
      )}
      {activeTab === "purchases" && <PurchasesSection config={config} />}
      {activeTab === "work_orders" && <WorkOrdersSection config={config} />}
      {activeTab === "sales" && <SalesSection config={config} />}
      {activeTab === "roles" && <RolesSection config={config} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section Components
// ---------------------------------------------------------------------------

function SectionHeader({ section, title }: { section: string; title: string }) {
  const resetMutation = useResetSection();
  return (
    <div className="flex items-center justify-between">
      <h3 className="text-sm font-medium text-[var(--text-primary)]">
        {title}
      </h3>
      <button
        onClick={() => {
          if (confirm(`Reset ${title} to system defaults?`))
            resetMutation.mutate(section, {
              onSuccess: () => toast.success(`${title} reset to defaults`),
              onError: () => toast.error(`Failed to reset ${title}`),
            });
        }}
        className="flex items-center gap-1 text-xs text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]"
      >
        <RotateCcw className="h-3 w-3" /> Reset to defaults
      </button>
    </div>
  );
}

function BusinessSection({
  config,
  onNavigateToLocations,
}: {
  config: OrgConfig;
  onNavigateToLocations: () => void;
}) {
  const update = useUpdateSection();
  const { data: locations } = useBusinessLocations();
  const biz = config.business as Record<string, unknown>;

  const invConfig = (config as unknown as Record<string, unknown>).inventory as
    | Record<string, unknown>
    | undefined;
  const [company, setCompany] = useState(config.business.company_name);
  const [currency, setCurrency] = useState(config.business.currency);
  const [timezone, setTimezone] = useState(config.business.timezone);
  const [taxRate, setTaxRate] = useState(
    String(config.business.default_tax_rate),
  );
  const [phone, setPhone] = useState(String(biz.phone || ""));
  const [email, setEmail] = useState(String(biz.email || ""));
  const [website, setWebsite] = useState(String(biz.website || ""));
  const [codeFormat, setCodeFormat] = useState(
    String(invConfig?.code_format || "barcode"),
  );

  useEffect(() => {
    const b = config.business as Record<string, unknown>;
    setCompany(config.business.company_name);
    setCurrency(config.business.currency);
    setTimezone(config.business.timezone);
    setTaxRate(String(config.business.default_tax_rate));
    setPhone(String(b.phone || ""));
    setEmail(String(b.email || ""));
    setWebsite(String(b.website || ""));
  }, [config]);

  const handleSave = () => {
    update.mutate(
      {
        section: "business",
        data: {
          company_name: company,
          currency,
          timezone,
          default_tax_rate: parseFloat(taxRate) || 0,
          phone,
          email,
          website,
          primary_address: biz.primary_address || {},
          business_locations: biz.business_locations || [],
        },
      },
      {
        onSuccess: () => {
          update.mutate({
            section: "inventory",
            data: { ...invConfig, code_format: codeFormat },
          });
          toast.success("Business profile saved");
        },
        onError: () => toast.error("Failed to save business profile"),
      },
    );
  };

  // Resolve the primary location for address display.
  const primaryLocation = locations?.find((l) => l.is_primary);
  const addr = primaryLocation?.address;

  return (
    <div className="space-y-6">
      <Card>
        <SectionHeader section="business" title="Business profile" />
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Input
              label="Company name"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder="Your Company LLC"
            />
          </div>
          <Input
            label="Phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="555-123-4567"
          />
          <Input
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="info@company.com"
          />
          <Input
            label="Website"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            placeholder="https://company.com"
          />
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Currency"
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              placeholder="USD"
            />
            <div>
              <Input
                label="Default tax rate (%)"
                type="number"
                step="0.01"
                value={taxRate}
                onChange={(e) => setTaxRate(e.target.value)}
                placeholder="0.00"
              />
              <p className="text-[11px] text-[var(--text-tertiary)] mt-1">
                Reference rate only. Order taxes use Tax Schemes.
              </p>
            </div>
          </div>
          <Input
            label="Timezone"
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            placeholder="America/Chicago"
          />
          <div>
            <label className="block text-[13px] font-medium text-[var(--text-primary)]">
              Label format
            </label>
            <select
              value={codeFormat}
              onChange={(e) => setCodeFormat(e.target.value)}
              className="mt-1.5 block w-full h-[38px] rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)]"
            >
              <option value="barcode">Barcode (Code 128)</option>
              <option value="qrcode">QR Code</option>
            </select>
            <p className="mt-1 text-xs text-[var(--text-tertiary)]">
              Choose the default code format for asset labels and scanning.
            </p>
          </div>
        </div>
      </Card>

      {/* Primary address — read-only, managed via Locations tab */}
      <Card>
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-sm font-medium text-[var(--text-primary)]">
              Primary business address
            </h3>
            <p className="mt-1 text-xs text-[var(--text-tertiary)]">
              This appears on printed documents, invoices, and public-facing
              pages. Managed on the Facilities tab.
            </p>
          </div>
          <button
            onClick={onNavigateToLocations}
            className="text-xs font-medium text-[var(--color-interactive-primary)] hover:underline shrink-0"
          >
            Edit on Facilities tab →
          </button>
        </div>
        {addr?.street1 || addr?.city ? (
          <div className="mt-3 rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--surface-sunken)]/50 px-4 py-3">
            {primaryLocation && (
              <p className="text-sm font-medium text-[var(--text-primary)]">
                {primaryLocation.name}
              </p>
            )}
            {addr?.street1 && (
              <p className="text-sm text-[var(--text-secondary)] mt-0.5">
                {addr.street1}
                {addr.street2 ? `, ${addr.street2}` : ""}
              </p>
            )}
            {addr?.city && (
              <p className="text-sm text-[var(--text-secondary)]">
                {addr.city}, {addr.state} {addr.zip}
              </p>
            )}
          </div>
        ) : (
          <div className="mt-3 rounded-[var(--radius-md)] border border-dashed border-[var(--border-default)] py-4 text-center">
            <p className="text-sm text-[var(--text-tertiary)]">
              No primary address set
            </p>
            <button
              onClick={onNavigateToLocations}
              className="mt-1 text-xs font-medium text-[var(--color-interactive-primary)] hover:underline"
            >
              Add a facility →
            </button>
          </div>
        )}
      </Card>

      <div className="flex justify-end">
        <Button size="sm" onClick={handleSave} isLoading={update.isPending}>
          <Save className="h-4 w-4" /> Save
        </Button>
      </div>
    </div>
  );
}

/** Products section — asset categories, makes/manufacturers, and models. */
function ProductsSection({ config }: { config: OrgConfig }) {
  const update = useUpdateSection();
  const products = config.products;
  const fallbackCats = config.inventory?.asset_categories || [];

  const [categories, setCategories] = useState(
    products?.asset_categories?.length > 0
      ? products.asset_categories
      : fallbackCats,
  );
  const [makes, setMakes] = useState<MakeOption[]>(
    (products?.makes as MakeOption[]) || [],
  );
  const [models, setModels] = useState<ModelOption[]>(
    (products?.models as ModelOption[]) || [],
  );
  const [variants, setVariants] = useState<VariantOption[]>(
    (products?.variants as VariantOption[]) || [],
  );
  const [namingConvention, setNamingConvention] = useState<NamingConvention>(
    products?.naming_convention || {
      components: [
        { key: "year", builtin: true },
        { key: "make", builtin: true },
        { key: "model", builtin: true },
        { key: "category", builtin: true },
      ],
      separator: " ",
    },
  );
  const [costTypes] = useState(
    products?.cost_types || config.inventory?.cost_types || [],
  );

  useEffect(() => {
    const p = config.products;
    if (p) {
      setCategories(
        p.asset_categories?.length > 0 ? p.asset_categories : fallbackCats,
      );
      setMakes((p.makes as MakeOption[]) || []);
      setModels((p.models as ModelOption[]) || []);
      setVariants((p.variants as VariantOption[]) || []);
      if (p.naming_convention) setNamingConvention(p.naming_convention);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  const handleSave = () => {
    update.mutate(
      {
        section: "products",
        data: {
          asset_categories: categories,
          naming_convention: namingConvention,
          variants,
          makes,
          models,
          cost_types: costTypes,
        },
      },
      {
        onSuccess: () => toast.success("Product settings saved"),
        onError: () => toast.error("Failed to save"),
      },
    );
  };

  return (
    <div className="space-y-6">
      {/* Naming Convention */}
      <Card>
        <NamingConventionEditor
          convention={namingConvention}
          onChange={setNamingConvention}
        />
      </Card>

      {/* Asset categories */}
      <Card>
        <SectionHeader section="products" title="Asset categories" />
        <p className="mt-1 text-xs text-[var(--text-tertiary)]">
          Hover to edit, delete, or add subcategories.
        </p>
        <div className="mt-4">
          <CategoryManager categories={categories} onChange={setCategories} />
        </div>
      </Card>

      {/* Makes & Models */}
      <Card>
        <MakeEditor
          makes={makes}
          models={models}
          categories={categories}
          onMakesChange={setMakes}
          onModelsChange={setModels}
        />
      </Card>

      {/* Variants */}
      <Card>
        <VariantsEditor variants={variants} onChange={setVariants} />
      </Card>

      <div className="flex justify-end">
        <Button size="sm" onClick={handleSave} isLoading={update.isPending}>
          <Save className="h-4 w-4" /> Save product settings
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Naming Convention Editor
// ---------------------------------------------------------------------------

/** Built-in component definitions with default labels and examples. */
const BUILTIN_COMPONENTS: Array<{
  key: string;
  label: string;
  description: string;
  example: string;
}> = [
  {
    key: "year",
    label: "Year",
    description: "Model year (e.g., 2024)",
    example: "2024",
  },
  {
    key: "make",
    label: "Make",
    description: "Manufacturer (e.g., John Deere)",
    example: "John Deere",
  },
  {
    key: "model",
    label: "Model",
    description: "Model name/number (e.g., 6120M)",
    example: "6120M",
  },
  {
    key: "variant",
    label: "Variant",
    description: "Trim, config, or package (e.g., Premium)",
    example: "Premium",
  },
  {
    key: "category",
    label: "Category",
    description: "Asset classification (e.g., Tractor)",
    example: "Tractor",
  },
];

const BUILTIN_KEYS = new Set(BUILTIN_COMPONENTS.map((c) => c.key));

/** Auto-generate a slug from a label. */
function slugify(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
}

/** Get the display label for a component. */
function getComponentLabel(comp: NamingComponent): string {
  if (comp.label) return comp.label;
  const builtin = BUILTIN_COMPONENTS.find((b) => b.key === comp.key);
  return builtin?.label || comp.key;
}

/** Get the description for a component. */
function getComponentDescription(comp: NamingComponent): string {
  if (comp.description) return comp.description;
  const builtin = BUILTIN_COMPONENTS.find((b) => b.key === comp.key);
  return builtin?.description || "";
}

/** Get an example value for a component (used in preview). */
function getComponentExample(comp: NamingComponent): string {
  if (comp.builtin) {
    const builtin = BUILTIN_COMPONENTS.find((b) => b.key === comp.key);
    return builtin?.example || comp.key;
  }
  if (comp.options && comp.options.length > 0) return comp.options[0]!;
  return `[${getComponentLabel(comp)}]`;
}

function NamingConventionEditor({
  convention,
  onChange,
}: {
  convention: NamingConvention;
  onChange: (c: NamingConvention) => void;
}) {
  const active = convention.components;
  const activeKeys = new Set(active.map((c) => c.key));
  const inactiveBuiltins = BUILTIN_COMPONENTS.filter(
    (b) => !activeKeys.has(b.key),
  );

  // --- Label editing state ---
  const [editingLabelKey, setEditingLabelKey] = useState<string | null>(null);
  const [editLabelValue, setEditLabelValue] = useState("");

  // --- Custom component creation state ---
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customLabel, setCustomLabel] = useState("");
  const [customDescription, setCustomDescription] = useState("");
  const [customInputType, setCustomInputType] = useState<"text" | "select">(
    "text",
  );
  const [customOptions, setCustomOptions] = useState<string[]>([]);
  const [customOptionInput, setCustomOptionInput] = useState("");

  // --- Custom component editing state ---
  const [editingCustomKey, setEditingCustomKey] = useState<string | null>(null);

  const addBuiltinComponent = (key: string) => {
    const builtin = BUILTIN_COMPONENTS.find((b) => b.key === key);
    if (!builtin) return;
    const comp: NamingComponent = { key, builtin: true };
    onChange({ ...convention, components: [...active, comp] });
  };

  const removeComponent = (key: string) => {
    onChange({
      ...convention,
      components: active.filter((c) => c.key !== key),
    });
  };

  const moveUp = (idx: number) => {
    if (idx <= 0) return;
    const next = [...active];
    [next[idx - 1], next[idx]] = [next[idx]!, next[idx - 1]!];
    onChange({ ...convention, components: next });
  };

  const moveDown = (idx: number) => {
    if (idx >= active.length - 1) return;
    const next = [...active];
    [next[idx], next[idx + 1]] = [next[idx + 1]!, next[idx]!];
    onChange({ ...convention, components: next });
  };

  const startLabelEdit = (comp: NamingComponent) => {
    setEditingLabelKey(comp.key);
    setEditLabelValue(getComponentLabel(comp));
  };

  const saveLabelEdit = useCallback(() => {
    if (!editingLabelKey) return;
    const trimmed = editLabelValue.trim();
    const updated = active.map((c) => {
      if (c.key !== editingLabelKey) return c;
      if (c.builtin) {
        const builtin = BUILTIN_COMPONENTS.find((b) => b.key === c.key);
        const isDefault = trimmed === builtin?.label || !trimmed;
        return isDefault
          ? { ...c, label: undefined }
          : { ...c, label: trimmed };
      }
      return { ...c, label: trimmed || c.label };
    });
    onChange({ ...convention, components: updated });
    setEditingLabelKey(null);
    setEditLabelValue("");
  }, [editingLabelKey, editLabelValue, active, convention, onChange]);

  const resetCustomForm = () => {
    setShowCustomForm(false);
    setCustomLabel("");
    setCustomDescription("");
    setCustomInputType("text");
    setCustomOptions([]);
    setCustomOptionInput("");
    setEditingCustomKey(null);
  };

  const addCustomOption = () => {
    const val = customOptionInput.trim();
    if (!val || customOptions.includes(val)) return;
    setCustomOptions([...customOptions, val]);
    setCustomOptionInput("");
  };

  const saveCustomComponent = () => {
    const label = customLabel.trim();
    if (!label) return;
    const key = editingCustomKey || slugify(label);

    if (!editingCustomKey && (BUILTIN_KEYS.has(key) || activeKeys.has(key))) {
      toast.error("A component with that key already exists");
      return;
    }

    const comp: NamingComponent = {
      key,
      builtin: false,
      label,
      description: customDescription.trim() || undefined,
      input_type: customInputType,
      options: customInputType === "select" ? customOptions : undefined,
    };

    if (editingCustomKey) {
      onChange({
        ...convention,
        components: active.map((c) => (c.key === editingCustomKey ? comp : c)),
      });
    } else {
      onChange({ ...convention, components: [...active, comp] });
    }
    resetCustomForm();
  };

  const startEditCustom = (comp: NamingComponent) => {
    setEditingCustomKey(comp.key);
    setCustomLabel(comp.label || "");
    setCustomDescription(comp.description || "");
    setCustomInputType(comp.input_type || "text");
    setCustomOptions(comp.options || []);
    setShowCustomForm(true);
  };

  const preview = active
    .map((c) => getComponentExample(c))
    .filter(Boolean)
    .join(convention.separator || " ");

  return (
    <div>
      <h3 className="text-sm font-medium text-[var(--text-primary)]">
        Naming Convention
      </h3>
      <p className="mt-1 text-xs text-[var(--text-tertiary)]">
        Choose which components appear in asset names and their order. Rename
        built-in components or add custom ones to fit your business.
      </p>

      {/* Preview */}
      <div className="mt-3 rounded-[var(--radius-md)] bg-[var(--surface-sunken)] px-3 py-2">
        <p className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)] mb-1">
          Preview
        </p>
        <p className="text-sm font-semibold text-[var(--text-primary)]">
          {preview || "No components selected"}
        </p>
      </div>

      {/* Active components (ordered) */}
      <div className="mt-3 space-y-1">
        {active.map((comp, idx) => (
          <div
            key={comp.key}
            className="flex items-center gap-2 rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--surface-base)] px-3 py-2"
          >
            <GripVertical className="h-3.5 w-3.5 text-[var(--text-tertiary)]" />
            <div className="flex-1 min-w-0">
              {editingLabelKey === comp.key ? (
                <div className="flex items-center gap-1.5">
                  <input
                    type="text"
                    value={editLabelValue}
                    onChange={(e) => setEditLabelValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") saveLabelEdit();
                      if (e.key === "Escape") setEditingLabelKey(null);
                    }}
                    autoFocus
                    className="text-sm font-medium text-[var(--text-primary)] bg-transparent border-b border-[var(--color-interactive-primary)] outline-none w-32"
                  />
                  <button
                    type="button"
                    onClick={saveLabelEdit}
                    className="text-[var(--color-interactive-primary)] text-[10px] font-medium"
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingLabelKey(null)}
                    className="text-[var(--text-tertiary)] text-[10px]"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-1.5">
                    <p className="text-sm font-medium text-[var(--text-primary)]">
                      {getComponentLabel(comp)}
                    </p>
                    {!comp.builtin && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[var(--color-interactive-primary)]/10 text-[var(--color-interactive-primary)] font-medium">
                        Custom
                      </span>
                    )}
                    {comp.builtin && comp.label && (
                      <span className="text-[9px] text-[var(--text-tertiary)] italic">
                        (
                        {
                          BUILTIN_COMPONENTS.find((b) => b.key === comp.key)
                            ?.label
                        }
                        )
                      </span>
                    )}
                  </div>
                  <p className="text-[10px] text-[var(--text-tertiary)]">
                    {getComponentDescription(comp)}
                    {!comp.builtin &&
                      comp.input_type === "select" &&
                      comp.options &&
                      ` — ${comp.options.length} option${comp.options.length !== 1 ? "s" : ""}`}
                  </p>
                </>
              )}
            </div>
            <div className="flex items-center gap-1">
              {/* Rename / Edit button */}
              {editingLabelKey !== comp.key && (
                <button
                  type="button"
                  onClick={() =>
                    comp.builtin ? startLabelEdit(comp) : startEditCustom(comp)
                  }
                  className="h-6 w-6 flex items-center justify-center rounded text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-sunken)] transition-colors"
                  title={comp.builtin ? "Rename label" : "Edit component"}
                >
                  {comp.builtin ? (
                    <Pencil className="h-3 w-3" />
                  ) : (
                    <Settings2 className="h-3 w-3" />
                  )}
                </button>
              )}
              <button
                type="button"
                onClick={() => moveUp(idx)}
                disabled={idx === 0}
                className="h-6 w-6 flex items-center justify-center rounded text-xs text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-sunken)] disabled:opacity-30 transition-colors"
              >
                &uarr;
              </button>
              <button
                type="button"
                onClick={() => moveDown(idx)}
                disabled={idx === active.length - 1}
                className="h-6 w-6 flex items-center justify-center rounded text-xs text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-sunken)] disabled:opacity-30 transition-colors"
              >
                &darr;
              </button>
              <button
                type="button"
                onClick={() => removeComponent(comp.key)}
                className="h-6 w-6 flex items-center justify-center rounded text-[var(--text-tertiary)] hover:text-[var(--text-danger)] hover:bg-[var(--fill-danger)] transition-colors"
                title="Remove from naming convention"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Available built-in components */}
      {inactiveBuiltins.length > 0 && (
        <div className="mt-3">
          <p className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)] mb-1.5">
            Available components
          </p>
          <div className="flex flex-wrap gap-1.5">
            {inactiveBuiltins.map((comp) => (
              <button
                key={comp.key}
                type="button"
                onClick={() => addBuiltinComponent(comp.key)}
                className="inline-flex items-center gap-1 rounded-full border border-dashed border-[var(--border-default)] px-2.5 py-1 text-xs text-[var(--text-secondary)] hover:border-[var(--color-interactive-primary)] hover:text-[var(--color-interactive-primary)] transition-colors"
              >
                <Plus className="h-3 w-3" />
                {comp.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Custom component form */}
      {showCustomForm ? (
        <div className="mt-3 rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-sunken)] p-3 space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-semibold text-[var(--text-primary)]">
              {editingCustomKey
                ? "Edit Custom Component"
                : "New Custom Component"}
            </h4>
            <button
              type="button"
              onClick={resetCustomForm}
              className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-medium uppercase tracking-wider text-[var(--text-tertiary)] mb-1">
                Label *
              </label>
              <input
                type="text"
                value={customLabel}
                onChange={(e) => setCustomLabel(e.target.value)}
                placeholder="e.g., Color, Material, Grade"
                className="w-full rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2.5 py-1.5 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus:border-[var(--color-interactive-primary)]"
              />
              {customLabel.trim() && !editingCustomKey && (
                <p className="text-[9px] text-[var(--text-tertiary)] mt-0.5">
                  Key: {slugify(customLabel)}
                </p>
              )}
            </div>
            <div>
              <label className="block text-[10px] font-medium uppercase tracking-wider text-[var(--text-tertiary)] mb-1">
                Description
              </label>
              <input
                type="text"
                value={customDescription}
                onChange={(e) => setCustomDescription(e.target.value)}
                placeholder="Help text for this field"
                className="w-full rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2.5 py-1.5 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus:border-[var(--color-interactive-primary)]"
              />
            </div>
          </div>

          {/* Input type toggle */}
          <div>
            <label className="block text-[10px] font-medium uppercase tracking-wider text-[var(--text-tertiary)] mb-1.5">
              Input Type
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setCustomInputType("text")}
                className={`px-3 py-1.5 rounded-[var(--radius-sm)] text-xs font-medium transition-colors ${
                  customInputType === "text"
                    ? "bg-[var(--color-interactive-primary)] text-white"
                    : "bg-[var(--surface-base)] text-[var(--text-secondary)] border border-[var(--border-default)] hover:border-[var(--border-strong)]"
                }`}
              >
                Free Text
              </button>
              <button
                type="button"
                onClick={() => setCustomInputType("select")}
                className={`px-3 py-1.5 rounded-[var(--radius-sm)] text-xs font-medium transition-colors ${
                  customInputType === "select"
                    ? "bg-[var(--color-interactive-primary)] text-white"
                    : "bg-[var(--surface-base)] text-[var(--text-secondary)] border border-[var(--border-default)] hover:border-[var(--border-strong)]"
                }`}
              >
                Select from List
              </button>
            </div>
          </div>

          {/* Options list (select type only) */}
          {customInputType === "select" && (
            <div>
              <label className="block text-[10px] font-medium uppercase tracking-wider text-[var(--text-tertiary)] mb-1.5">
                Options
              </label>
              {customOptions.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {customOptions.map((opt) => (
                    <span
                      key={opt}
                      className="inline-flex items-center gap-1 rounded-full bg-[var(--surface-base)] border border-[var(--border-subtle)] px-2 py-0.5 text-xs text-[var(--text-primary)]"
                    >
                      {opt}
                      <button
                        type="button"
                        onClick={() =>
                          setCustomOptions(
                            customOptions.filter((o) => o !== opt),
                          )
                        }
                        className="text-[var(--text-tertiary)] hover:text-[var(--text-danger)]"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div className="flex gap-1.5">
                <input
                  type="text"
                  value={customOptionInput}
                  onChange={(e) => setCustomOptionInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addCustomOption();
                    }
                  }}
                  placeholder="Add an option..."
                  className="flex-1 rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2.5 py-1.5 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus:border-[var(--color-interactive-primary)]"
                />
                <button
                  type="button"
                  onClick={addCustomOption}
                  disabled={!customOptionInput.trim()}
                  className="px-2.5 py-1.5 rounded-[var(--radius-sm)] text-xs font-medium bg-[var(--surface-base)] border border-[var(--border-default)] text-[var(--text-secondary)] hover:border-[var(--border-strong)] disabled:opacity-30 transition-colors"
                >
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          )}

          {/* Save / Cancel */}
          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={resetCustomForm}
              className="px-3 py-1.5 rounded-[var(--radius-sm)] text-xs text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={saveCustomComponent}
              disabled={
                !customLabel.trim() ||
                (customInputType === "select" && customOptions.length === 0)
              }
              className="px-3 py-1.5 rounded-[var(--radius-sm)] text-xs font-medium bg-[var(--color-interactive-primary)] text-white hover:opacity-90 disabled:opacity-30 transition-colors"
            >
              {editingCustomKey ? "Update Component" : "Add Component"}
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setShowCustomForm(true)}
          className="mt-3 inline-flex items-center gap-1.5 rounded-[var(--radius-sm)] border border-dashed border-[var(--border-default)] px-3 py-1.5 text-xs text-[var(--text-secondary)] hover:border-[var(--color-interactive-primary)] hover:text-[var(--color-interactive-primary)] transition-colors"
        >
          <Plus className="h-3 w-3" />
          Custom Component
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Variants Editor
// ---------------------------------------------------------------------------

function VariantsEditor({
  variants,
  onChange,
}: {
  variants: VariantOption[];
  onChange: (v: VariantOption[]) => void;
}) {
  const [newName, setNewName] = useState("");

  const addVariant = () => {
    const name = newName.trim();
    if (!name) return;
    if (variants.some((v) => v.name.toLowerCase() === name.toLowerCase())) {
      toast.error("Variant already exists");
      return;
    }
    onChange([...variants, { id: `var-${Date.now()}`, name }]);
    setNewName("");
  };

  const removeVariant = (id: string) => {
    onChange(variants.filter((v) => v.id !== id));
  };

  return (
    <div>
      <h3 className="text-sm font-medium text-[var(--text-primary)]">
        Variants
      </h3>
      <p className="mt-1 text-xs text-[var(--text-tertiary)]">
        Trim levels, configurations, or packages (e.g., Premium, MFWD, 4WD).
        These help the tokenizer recognize variant names in asset names.
      </p>

      <div className="mt-3 flex items-center gap-2">
        <input
          type="text"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addVariant();
            }
          }}
          placeholder="Add a variant..."
          className="flex-1 h-8 rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus-visible:shadow-[var(--focus-ring)]"
        />
        <Button
          size="sm"
          variant="secondary"
          onClick={addVariant}
          disabled={!newName.trim()}
        >
          <Plus className="h-3.5 w-3.5" /> Add
        </Button>
      </div>

      {variants.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {variants.map((v) => (
            <span
              key={v.id}
              className="inline-flex items-center gap-1 rounded-full bg-[var(--surface-sunken)] px-2.5 py-1 text-xs text-[var(--text-primary)]"
            >
              {v.name}
              <button
                type="button"
                onClick={() => removeVariant(v.id)}
                className="ml-0.5 text-[var(--text-tertiary)] hover:text-[var(--text-danger)] transition-colors"
              >
                &times;
              </button>
            </span>
          ))}
        </div>
      )}

      {variants.length === 0 && (
        <p className="mt-3 text-xs text-[var(--text-tertiary)]">
          No variants defined yet. Add variants to enable the variant component
          in asset names.
        </p>
      )}
    </div>
  );
}

/** Locations tab — business locations (with inline storage areas) and storage schemes. */
function LocationManagerTab() {
  return (
    <div className="space-y-6">
      <Card>
        <LocationManager />
      </Card>
      <Card>
        <LocationSchemeManager />
      </Card>
    </div>
  );
}

/**
 * Reusable card for a single number format type.
 *
 * Each card shows: enabled toggle, prefix, digits, live preview.
 * Consistent layout across SKU, PO, SO, WO, and Lot types.
 */
function NumberFormatCard({
  title,
  description,
  enabled,
  onEnabledChange,
  prefix,
  onPrefixChange,
  digits,
  onDigitsChange,
  preview,
  startNumber,
  onStartNumberChange,
}: {
  title: string;
  description: string;
  enabled: boolean;
  onEnabledChange: (v: boolean) => void;
  prefix: string;
  onPrefixChange: (v: string) => void;
  digits: string;
  onDigitsChange: (v: string) => void;
  preview: string;
  startNumber?: string;
  onStartNumberChange?: (v: string) => void;
}) {
  return (
    <div
      className={`rounded-[var(--radius-lg)] border p-4 transition-colors ${
        enabled
          ? "border-[var(--border-default)] bg-[var(--surface-base)]"
          : "border-[var(--border-subtle)] bg-[var(--surface-sunken)]/50 opacity-60"
      }`}
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="text-sm font-semibold text-[var(--text-primary)]">
            {title}
          </p>
          <p className="text-[11px] text-[var(--text-tertiary)] mt-0.5">
            {description}
          </p>
        </div>
        <label className="flex items-center gap-2 text-xs cursor-pointer shrink-0">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => onEnabledChange(e.target.checked)}
            className="rounded border-[var(--border-default)]"
          />
          <span className="text-[var(--text-secondary)]">Enabled</span>
        </label>
      </div>

      {enabled && (
        <>
          <div
            className={`grid gap-3 ${startNumber !== undefined ? "grid-cols-3" : "grid-cols-2"}`}
          >
            <div>
              <label className="block text-[10px] font-medium uppercase tracking-wider text-[var(--text-tertiary)] mb-1">
                Prefix
              </label>
              <input
                type="text"
                value={prefix}
                onChange={(e) => onPrefixChange(e.target.value.toUpperCase())}
                className="block w-full h-8 rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2 text-sm font-mono text-[var(--text-primary)] focus:outline-none focus-visible:shadow-[var(--focus-ring)]"
              />
            </div>
            <div>
              <label className="block text-[10px] font-medium uppercase tracking-wider text-[var(--text-tertiary)] mb-1">
                Digits
              </label>
              <input
                type="number"
                min="4"
                max="10"
                value={digits}
                onChange={(e) => onDigitsChange(e.target.value)}
                className="block w-full h-8 rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2 text-sm text-[var(--text-primary)] focus:outline-none focus-visible:shadow-[var(--focus-ring)] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
            </div>
            {startNumber !== undefined && onStartNumberChange && (
              <div>
                <label className="block text-[10px] font-medium uppercase tracking-wider text-[var(--text-tertiary)] mb-1">
                  Starting No.
                </label>
                <input
                  type="number"
                  min="1"
                  value={startNumber}
                  onChange={(e) => onStartNumberChange(e.target.value)}
                  className="block w-full h-8 rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2 text-sm text-[var(--text-primary)] focus:outline-none focus-visible:shadow-[var(--focus-ring)] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
              </div>
            )}
          </div>
          <div className="mt-3">
            <label className="block text-[10px] font-medium uppercase tracking-wider text-[var(--text-tertiary)] mb-1">
              Preview
            </label>
            <p className="rounded-[var(--radius-sm)] bg-[var(--surface-sunken)] px-3 py-1.5 font-mono text-sm text-[var(--text-primary)]">
              {preview}
            </p>
          </div>
        </>
      )}
    </div>
  );
}

function NumberFormatsSection({ config }: { config: OrgConfig }) {
  const update = useUpdateSection();
  const nf = config.number_formats;

  const [skuPrefix, setSkuPrefix] = useState(nf.sku_prefix);
  const [skuDigits, setSkuDigits] = useState(String(nf.sku_digits));
  const [skuEnabled, setSkuEnabled] = useState(nf.sku_enabled ?? true);
  const [poPrefix, setPoPrefix] = useState(nf.po_prefix);
  const [poDigits, setPoDigits] = useState(String(nf.po_digits ?? 6));
  const [poEnabled, setPoEnabled] = useState(nf.po_enabled ?? true);
  const [soPrefix, setSoPrefix] = useState(nf.so_prefix);
  const [soDigits, setSoDigits] = useState(String(nf.so_digits ?? 6));
  const [soEnabled, setSoEnabled] = useState(nf.so_enabled ?? true);
  const [woPrefix, setWoPrefix] = useState(nf.wo_prefix);
  const [woDigits, setWoDigits] = useState(String(nf.wo_digits ?? 6));
  const [woEnabled, setWoEnabled] = useState(nf.wo_enabled ?? true);
  const [lotPrefix, setLotPrefix] = useState(nf.lot_prefix ?? "LOT");
  const [lotDigits, setLotDigits] = useState(String(nf.lot_digits ?? 6));
  const [lotStart, setLotStart] = useState(String(nf.lot_start ?? 1));
  const [lotEnabled, setLotEnabled] = useState(nf.lot_auto_generate ?? true);

  useEffect(() => {
    const n = config.number_formats;
    setSkuPrefix(n.sku_prefix);
    setSkuDigits(String(n.sku_digits));
    setSkuEnabled(n.sku_enabled ?? true);
    setPoPrefix(n.po_prefix);
    setPoDigits(String(n.po_digits ?? 6));
    setPoEnabled(n.po_enabled ?? true);
    setSoPrefix(n.so_prefix);
    setSoDigits(String(n.so_digits ?? 6));
    setSoEnabled(n.so_enabled ?? true);
    setWoPrefix(n.wo_prefix);
    setWoDigits(String(n.wo_digits ?? 6));
    setWoEnabled(n.wo_enabled ?? true);
    setLotPrefix(n.lot_prefix ?? "LOT");
    setLotDigits(String(n.lot_digits ?? 6));
    setLotStart(String(n.lot_start ?? 1));
    setLotEnabled(n.lot_auto_generate ?? true);
  }, [config]);

  const handleSave = () => {
    update.mutate(
      {
        section: "number_formats",
        data: {
          sku_prefix: skuPrefix,
          sku_digits: parseInt(skuDigits) || 6,
          sku_enabled: skuEnabled,
          po_prefix: poPrefix,
          po_digits: parseInt(poDigits) || 6,
          po_enabled: poEnabled,
          so_prefix: soPrefix,
          so_digits: parseInt(soDigits) || 6,
          so_enabled: soEnabled,
          wo_prefix: woPrefix,
          wo_digits: parseInt(woDigits) || 6,
          wo_enabled: woEnabled,
          lot_prefix: lotPrefix,
          lot_digits: parseInt(lotDigits) || 6,
          lot_start: parseInt(lotStart) || 1,
          lot_auto_generate: lotEnabled,
        },
      },
      {
        onSuccess: () => toast.success("Number formats saved"),
        onError: () => toast.error("Failed to save number formats"),
      },
    );
  };

  const pad = (d: string) => "0".repeat(Math.max((parseInt(d) || 6) - 1, 0));

  return (
    <Card>
      <SectionHeader section="number_formats" title="Number Formats" />

      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <NumberFormatCard
          title="Asset SKUs"
          description="Unique identifiers for inventory assets"
          enabled={skuEnabled}
          onEnabledChange={setSkuEnabled}
          prefix={skuPrefix}
          onPrefixChange={setSkuPrefix}
          digits={skuDigits}
          onDigitsChange={setSkuDigits}
          preview={`${skuPrefix ? `${skuPrefix}-` : ""}EQ-${pad(skuDigits)}1`}
        />

        <NumberFormatCard
          title="Purchase Orders"
          description="Sequential numbers for procurement"
          enabled={poEnabled}
          onEnabledChange={setPoEnabled}
          prefix={poPrefix}
          onPrefixChange={setPoPrefix}
          digits={poDigits}
          onDigitsChange={setPoDigits}
          preview={`${poPrefix ? `${poPrefix}-` : ""}${pad(poDigits)}1`}
        />

        <NumberFormatCard
          title="Sales Orders"
          description="Sequential numbers for customer sales"
          enabled={soEnabled}
          onEnabledChange={setSoEnabled}
          prefix={soPrefix}
          onPrefixChange={setSoPrefix}
          digits={soDigits}
          onDigitsChange={setSoDigits}
          preview={`${soPrefix ? `${soPrefix}-` : ""}${pad(soDigits)}1`}
        />

        <NumberFormatCard
          title="Work Orders"
          description="Sequential numbers for service & repairs"
          enabled={woEnabled}
          onEnabledChange={setWoEnabled}
          prefix={woPrefix}
          onPrefixChange={setWoPrefix}
          digits={woDigits}
          onDigitsChange={setWoDigits}
          preview={`${woPrefix ? `${woPrefix}-` : ""}${pad(woDigits)}1`}
        />

        <NumberFormatCard
          title="Lot Numbers"
          description="Auto-generated identifiers for asset lots"
          enabled={lotEnabled}
          onEnabledChange={setLotEnabled}
          prefix={lotPrefix}
          onPrefixChange={setLotPrefix}
          digits={lotDigits}
          onDigitsChange={setLotDigits}
          preview={`${lotPrefix ? `${lotPrefix}-` : ""}${pad(lotDigits)}${parseInt(lotStart) || 1}`}
          startNumber={lotStart}
          onStartNumberChange={setLotStart}
        />
      </div>

      <div className="mt-5 flex justify-end">
        <Button size="sm" onClick={handleSave} isLoading={update.isPending}>
          <Save className="h-4 w-4" /> Save number formats
        </Button>
      </div>
    </Card>
  );
}

/** Purchases section — procurement defaults, payment terms, shipping methods, charge types. */
function PurchasesSection({ config }: { config: OrgConfig }) {
  const update = useUpdateSection();
  const pc = config.purchases;

  const [currency, setCurrency] = useState(pc?.default_currency || "USD");
  const [taxRate, setTaxRate] = useState(
    String((pc?.default_tax_rate || 0) * 100),
  );
  const [paymentTerms, setPaymentTerms] = useState(pc?.payment_terms || []);
  const [shippingMethods, setShippingMethods] = useState(
    pc?.shipping_methods || [],
  );
  const [chargeTypes, setChargeTypes] = useState(pc?.charge_types || []);

  const [newTerm, setNewTerm] = useState({ value: "", label: "" });
  const [newShipping, setNewShipping] = useState({ value: "", label: "" });
  const [newCharge, setNewCharge] = useState({ value: "", label: "" });

  useEffect(() => {
    const p = config.purchases;
    if (p) {
      setCurrency(p.default_currency || "USD");
      setTaxRate(String((p.default_tax_rate || 0) * 100));
      setPaymentTerms(p.payment_terms || []);
      setShippingMethods(p.shipping_methods || []);
      setChargeTypes(p.charge_types || []);
    }
  }, [config]);

  const handleSave = () => {
    update.mutate(
      {
        section: "purchases",
        data: {
          default_currency: currency,
          default_tax_rate: (parseFloat(taxRate) || 0) / 100,
          payment_terms: paymentTerms,
          shipping_methods: shippingMethods,
          charge_types: chargeTypes,
        },
      },
      {
        onSuccess: () => toast.success("Purchase settings saved"),
        onError: () => toast.error("Failed to save purchase settings"),
      },
    );
  };

  return (
    <div className="space-y-6">
      {/* Defaults */}
      <Card>
        <SectionHeader section="purchases" title="Purchase Defaults" />
        <p className="mt-1 text-xs text-[var(--text-tertiary)]">
          Default values applied to new purchase orders.
        </p>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-[var(--text-secondary)]">
              Default Currency
            </label>
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className="mt-1 block w-full rounded-[var(--radius-md)] border border-[var(--border-default)] px-3 py-2 text-sm"
            >
              <option value="USD">USD — US Dollar</option>
              <option value="CAD">CAD — Canadian Dollar</option>
              <option value="EUR">EUR — Euro</option>
              <option value="GBP">GBP — British Pound</option>
              <option value="MXN">MXN — Mexican Peso</option>
              <option value="AUD">AUD — Australian Dollar</option>
            </select>
          </div>
          <div>
            <Input
              label="Default Tax Rate (%)"
              type="number"
              step="0.01"
              min="0"
              value={taxRate}
              onChange={(e) => setTaxRate(e.target.value)}
              placeholder="0.00"
            />
            <p className="text-[11px] text-[var(--text-tertiary)] mt-1">
              Reference rate only. Automated tax calculations use Tax Schemes
              configured in Admin &rarr; Tax Schemes.
            </p>
          </div>
        </div>
      </Card>

      {/* Payment Terms */}
      <Card>
        <h3 className="text-sm font-semibold text-[var(--text-primary)]">
          Payment Terms
        </h3>
        <p className="mt-1 text-xs text-[var(--text-tertiary)]">
          Standard payment terms available when creating purchase orders.
        </p>
        <div className="mt-4 space-y-2">
          {paymentTerms.map((t, i) => (
            <div
              key={i}
              className="flex items-center justify-between rounded-[var(--radius-sm)] border border-[var(--border-default)] px-3 py-2"
            >
              <span className="text-sm">{t.label}</span>
              <button
                onClick={() =>
                  setPaymentTerms(paymentTerms.filter((_, j) => j !== i))
                }
                className="text-[var(--text-tertiary)] hover:text-[var(--text-danger)]"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          <div className="flex gap-2">
            <input
              placeholder="Value (e.g., net_30)"
              value={newTerm.value}
              onChange={(e) =>
                setNewTerm({ ...newTerm, value: e.target.value })
              }
              className="flex-1 rounded-[var(--radius-md)] border border-[var(--border-default)] px-3 py-1.5 text-sm"
            />
            <input
              placeholder="Label (e.g., Net 30)"
              value={newTerm.label}
              onChange={(e) =>
                setNewTerm({ ...newTerm, label: e.target.value })
              }
              className="flex-1 rounded-[var(--radius-md)] border border-[var(--border-default)] px-3 py-1.5 text-sm"
            />
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                if (newTerm.value && newTerm.label) {
                  setPaymentTerms([...paymentTerms, newTerm]);
                  setNewTerm({ value: "", label: "" });
                }
              }}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Card>

      {/* Shipping Methods */}
      <Card>
        <h3 className="text-sm font-semibold text-[var(--text-primary)]">
          Shipping Methods
        </h3>
        <p className="mt-1 text-xs text-[var(--text-tertiary)]">
          Available shipping/delivery methods for purchase orders.
        </p>
        <div className="mt-4 space-y-2">
          {shippingMethods.map((m, i) => (
            <div
              key={i}
              className="flex items-center justify-between rounded-[var(--radius-sm)] border border-[var(--border-default)] px-3 py-2"
            >
              <span className="text-sm">{m.label}</span>
              <button
                onClick={() =>
                  setShippingMethods(shippingMethods.filter((_, j) => j !== i))
                }
                className="text-[var(--text-tertiary)] hover:text-[var(--text-danger)]"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          <div className="flex gap-2">
            <input
              placeholder="Value (e.g., freight)"
              value={newShipping.value}
              onChange={(e) =>
                setNewShipping({ ...newShipping, value: e.target.value })
              }
              className="flex-1 rounded-[var(--radius-md)] border border-[var(--border-default)] px-3 py-1.5 text-sm"
            />
            <input
              placeholder="Label (e.g., Freight / LTL)"
              value={newShipping.label}
              onChange={(e) =>
                setNewShipping({ ...newShipping, label: e.target.value })
              }
              className="flex-1 rounded-[var(--radius-md)] border border-[var(--border-default)] px-3 py-1.5 text-sm"
            />
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                if (newShipping.value && newShipping.label) {
                  setShippingMethods([...shippingMethods, newShipping]);
                  setNewShipping({ value: "", label: "" });
                }
              }}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Card>

      {/* Charge Types */}
      <Card>
        <h3 className="text-sm font-semibold text-[var(--text-primary)]">
          Line Charge Types
        </h3>
        <p className="mt-1 text-xs text-[var(--text-tertiary)]">
          Types of additional charges that can be added to purchase order line
          items (e.g., prep fees, inspection costs, registration).
        </p>
        <div className="mt-4 space-y-2">
          {chargeTypes.map((c, i) => (
            <div
              key={i}
              className="flex items-center justify-between rounded-[var(--radius-sm)] border border-[var(--border-default)] px-3 py-2"
            >
              <span className="text-sm">{c.label}</span>
              <button
                onClick={() =>
                  setChargeTypes(chargeTypes.filter((_, j) => j !== i))
                }
                className="text-[var(--text-tertiary)] hover:text-[var(--text-danger)]"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          <div className="flex gap-2">
            <input
              placeholder="Value (e.g., inspection)"
              value={newCharge.value}
              onChange={(e) =>
                setNewCharge({ ...newCharge, value: e.target.value })
              }
              className="flex-1 rounded-[var(--radius-md)] border border-[var(--border-default)] px-3 py-1.5 text-sm"
            />
            <input
              placeholder="Label (e.g., Inspection)"
              value={newCharge.label}
              onChange={(e) =>
                setNewCharge({ ...newCharge, label: e.target.value })
              }
              className="flex-1 rounded-[var(--radius-md)] border border-[var(--border-default)] px-3 py-1.5 text-sm"
            />
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                if (newCharge.value && newCharge.label) {
                  setChargeTypes([...chargeTypes, newCharge]);
                  setNewCharge({ value: "", label: "" });
                }
              }}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Card>

      <div className="flex justify-end">
        <Button size="sm" onClick={handleSave} isLoading={update.isPending}>
          <Save className="h-4 w-4" /> Save Purchase Settings
        </Button>
      </div>
    </div>
  );
}

function WorkOrdersSection({ config }: { config: OrgConfig }) {
  const update = useUpdateSection();
  const [laborRate, setLaborRate] = useState(
    String(config.work_orders.default_labor_rate),
  );
  const [workTypes, setWorkTypes] = useState(config.work_orders.work_types);
  const [priorities, setPriorities] = useState(config.work_orders.priorities);
  const [newType, setNewType] = useState({ value: "", label: "" });
  const [newPri, setNewPri] = useState({
    value: "",
    label: "",
    color: "#6B7280",
  });

  useEffect(() => {
    setLaborRate(String(config.work_orders.default_labor_rate));
    setWorkTypes(config.work_orders.work_types);
    setPriorities(config.work_orders.priorities);
  }, [config]);

  const handleSave = () => {
    update.mutate(
      {
        section: "work_orders",
        data: {
          default_labor_rate: parseFloat(laborRate) || 0,
          work_types: workTypes,
          priorities,
        },
      },
      {
        onSuccess: () => toast.success("Work order settings saved"),
        onError: () => toast.error("Failed to save work order settings"),
      },
    );
  };

  return (
    <div className="space-y-6">
      <Card>
        <SectionHeader section="work_orders" title="Default Labor Rate" />
        <p className="mt-1 text-xs text-[var(--text-tertiary)]">
          Used as the default hourly rate when logging labor on work orders if
          no rate is specified.
        </p>
        <div className="mt-4 max-w-xs">
          <Input
            label="Default Hourly Rate ($)"
            type="number"
            step="0.01"
            min="0"
            value={laborRate}
            onChange={(e) => setLaborRate(e.target.value)}
            placeholder="0.00"
          />
        </div>
      </Card>

      <Card>
        <SectionHeader section="work_orders" title="Work Types" />
        <div className="mt-4 space-y-2">
          {workTypes.map((wt, i) => (
            <div
              key={i}
              className="flex items-center justify-between rounded border border-[var(--border-default)] px-3 py-2"
            >
              <span className="text-sm font-medium">{wt.label}</span>
              <button
                onClick={() =>
                  setWorkTypes(workTypes.filter((_, j) => j !== i))
                }
                className="text-[var(--text-tertiary)] hover:text-[var(--text-danger)]"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          <div className="flex gap-2">
            <input
              placeholder="Value"
              value={newType.value}
              onChange={(e) =>
                setNewType({ ...newType, value: e.target.value })
              }
              className="flex-1 rounded-[var(--radius-md)] border border-[var(--border-default)] px-3 py-1.5 text-sm"
            />
            <input
              placeholder="Label"
              value={newType.label}
              onChange={(e) =>
                setNewType({ ...newType, label: e.target.value })
              }
              className="flex-1 rounded-[var(--radius-md)] border border-[var(--border-default)] px-3 py-1.5 text-sm"
            />
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                if (newType.value && newType.label) {
                  setWorkTypes([...workTypes, newType]);
                  setNewType({ value: "", label: "" });
                }
              }}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Card>

      <Card>
        <h3 className="text-sm font-medium text-[var(--text-primary)]">
          Priorities
        </h3>
        <div className="mt-4 space-y-2">
          {priorities.map((p, i) => (
            <div
              key={i}
              className="flex items-center gap-3 rounded border border-[var(--border-default)] px-3 py-2"
            >
              <div
                className="h-4 w-4 rounded-full"
                style={{ backgroundColor: p.color }}
              />
              <span className="flex-1 text-sm font-medium">{p.label}</span>
              <button
                onClick={() =>
                  setPriorities(priorities.filter((_, j) => j !== i))
                }
                className="text-[var(--text-tertiary)] hover:text-[var(--text-danger)]"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          <div className="flex gap-2">
            <input
              placeholder="Value"
              value={newPri.value}
              onChange={(e) => setNewPri({ ...newPri, value: e.target.value })}
              className="flex-1 rounded-[var(--radius-md)] border border-[var(--border-default)] px-3 py-1.5 text-sm"
            />
            <input
              placeholder="Label"
              value={newPri.label}
              onChange={(e) => setNewPri({ ...newPri, label: e.target.value })}
              className="flex-1 rounded-[var(--radius-md)] border border-[var(--border-default)] px-3 py-1.5 text-sm"
            />
            <input
              type="color"
              value={newPri.color}
              onChange={(e) => setNewPri({ ...newPri, color: e.target.value })}
              className="h-9 w-9 cursor-pointer rounded border border-[var(--border-default)]"
            />
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                if (newPri.value && newPri.label) {
                  setPriorities([...priorities, newPri]);
                  setNewPri({ value: "", label: "", color: "#6B7280" });
                }
              }}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Card>

      <div className="flex justify-end">
        <Button size="sm" onClick={handleSave} isLoading={update.isPending}>
          <Save className="h-4 w-4" /> Save Work Order Settings
        </Button>
      </div>
    </div>
  );
}

function SalesSection({ config }: { config: OrgConfig }) {
  const update = useUpdateSection();
  const [rate, setRate] = useState(
    String(config.sales.default_commission_rate * 100),
  );
  const [basis, setBasis] = useState(config.sales.default_commission_basis);
  const [methods, setMethods] = useState(config.sales.payment_methods);
  const [newMethod, setNewMethod] = useState({ value: "", label: "" });

  useEffect(() => {
    setRate(String(config.sales.default_commission_rate * 100));
    setBasis(config.sales.default_commission_basis);
    setMethods(config.sales.payment_methods);
  }, [config]);

  const handleSave = () => {
    update.mutate(
      {
        section: "sales",
        data: {
          default_commission_rate: (parseFloat(rate) || 0) / 100,
          default_commission_basis: basis,
          payment_methods: methods,
        },
      },
      {
        onSuccess: () => toast.success("Sales settings saved"),
        onError: () => toast.error("Failed to save sales settings"),
      },
    );
  };

  return (
    <div className="space-y-6">
      <Card>
        <SectionHeader section="sales" title="Commission Defaults" />
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="Default Commission Rate (%)"
            type="number"
            step="0.1"
            value={rate}
            onChange={(e) => setRate(e.target.value)}
            placeholder="5.0"
          />
          <div>
            <label className="block text-sm font-medium text-[var(--text-secondary)]">
              Commission Basis
            </label>
            <select
              value={basis}
              onChange={(e) => setBasis(e.target.value)}
              className="mt-1 block w-full rounded-[var(--radius-md)] border border-[var(--border-default)] px-3 py-2 text-sm"
            >
              <option value="discounted_subtotal">
                Discounted Subtotal (net of discounts)
              </option>
              <option value="sale_price">Sale Price (before discounts)</option>
              <option value="gross_profit">Gross Profit</option>
            </select>
            <p className="text-[11px] text-[var(--text-tertiary)] mt-1">
              Applied when an order is confirmed. Existing commission records
              keep the basis they were calculated on.
            </p>
          </div>
        </div>
      </Card>

      <Card>
        <h3 className="text-sm font-medium text-[var(--text-primary)]">
          Payment Methods
        </h3>
        <div className="mt-4 space-y-2">
          {methods.map((m, i) => (
            <div
              key={i}
              className="flex items-center justify-between rounded border border-[var(--border-default)] px-3 py-2"
            >
              <span className="text-sm font-medium">{m.label}</span>
              <button
                onClick={() => setMethods(methods.filter((_, j) => j !== i))}
                className="text-[var(--text-tertiary)] hover:text-[var(--text-danger)]"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          <div className="flex gap-2">
            <input
              placeholder="Value"
              value={newMethod.value}
              onChange={(e) =>
                setNewMethod({ ...newMethod, value: e.target.value })
              }
              className="flex-1 rounded-[var(--radius-md)] border border-[var(--border-default)] px-3 py-1.5 text-sm"
            />
            <input
              placeholder="Label"
              value={newMethod.label}
              onChange={(e) =>
                setNewMethod({ ...newMethod, label: e.target.value })
              }
              className="flex-1 rounded-[var(--radius-md)] border border-[var(--border-default)] px-3 py-1.5 text-sm"
            />
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                if (newMethod.value && newMethod.label) {
                  setMethods([...methods, newMethod]);
                  setNewMethod({ value: "", label: "" });
                }
              }}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Card>

      <div className="flex justify-end">
        <Button size="sm" onClick={handleSave} isLoading={update.isPending}>
          <Save className="h-4 w-4" /> Save Sales Settings
        </Button>
      </div>
    </div>
  );
}

function RolesSection({ config }: { config: OrgConfig }) {
  const update = useUpdateSection();
  const [roles, setRoles] = useState(config.roles.roles);
  const [newRole, setNewRole] = useState({ value: "", label: "" });

  useEffect(() => {
    setRoles(config.roles.roles);
  }, [config]);

  const handleSave = () => {
    update.mutate(
      { section: "roles", data: { roles } },
      {
        onSuccess: () => toast.success("Roles saved"),
        onError: () => toast.error("Failed to save roles"),
      },
    );
  };

  return (
    <Card>
      <SectionHeader section="roles" title="User Roles" />
      <div className="mt-4 space-y-2">
        {roles.map((r, i) => (
          <div
            key={i}
            className="flex items-center justify-between rounded border border-[var(--border-default)] px-3 py-2"
          >
            <span className="text-sm font-medium">{r.label}</span>
            <span className="text-xs text-[var(--text-tertiary)]">
              {r.value}
            </span>
          </div>
        ))}
        <div className="flex gap-2">
          <input
            placeholder="Value (e.g., foreman)"
            value={newRole.value}
            onChange={(e) => setNewRole({ ...newRole, value: e.target.value })}
            className="flex-1 rounded-[var(--radius-md)] border border-[var(--border-default)] px-3 py-1.5 text-sm"
          />
          <input
            placeholder="Label (e.g., Foreman)"
            value={newRole.label}
            onChange={(e) => setNewRole({ ...newRole, label: e.target.value })}
            className="flex-1 rounded-[var(--radius-md)] border border-[var(--border-default)] px-3 py-1.5 text-sm"
          />
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              if (newRole.value && newRole.label) {
                setRoles([...roles, newRole]);
                setNewRole({ value: "", label: "" });
              }
            }}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <div className="mt-4 flex justify-end">
        <Button size="sm" onClick={handleSave} isLoading={update.isPending}>
          <Save className="h-4 w-4" /> Save
        </Button>
      </div>
    </Card>
  );
}

export default SettingsPage;
